import { defineComponent, ref, withKeys } from 'vue';
import { IGetDictionaryListRes, IGetDictionaryListReq } from '@/api/system-config/type.d';
import { useList } from '@/hooks/use-list';
import {
  getDictionaryList,
  delDictionary,
  updateDictionaryStatus,
  uploadDictionaryValueExcel,
} from '@/api/system-config/index';
import { GOODS_SERVICE_ERROR_LOG_STATE_LIST, DICTIONARY_STATUS_ENUM } from '@/constant';
import EditDialog from './components/edit-dialog';
import ExportColumnsDialog from './components/export-columns-dialog';
import ValueListDialog from './components/value-list-dialog';
import { ElMessage, ElMessageBox } from 'element-plus';
import CustomTable from '@/components/custom-table/index';
import { renderColumns } from './tableData';
import styles from './style.module.scss';
import usePermissionConfig from './use-permission-config';

export default defineComponent({
  components: {
    CustomTable,
    EditDialog,
    ValueListDialog,
    ExportColumnsDialog,
  },
  setup() {
    const selectedKeys = ref<string[]>([]);
    const {
      params,
      tableTotal,
      tableData,
      tableLoading,
      handleSearch,
      handleReset,
      handleSizeChange,
      handleCurrentChange,
    } = useList<IGetDictionaryListRes['list'][0], IGetDictionaryListReq>({
      request: {
        api: getDictionaryList,
        params: {
          pageNum: 1,
          pageSize: 10,
          code: '',
          name: '',
        },
        handleParams(paramsObj) {
          selectedKeys.value = [];
          if (paramsObj.code === '') {
            delete paramsObj.code;
            paramsObj.parentDictId = '0';
          } else {
            paramsObj.parentDictId = '';
          }
          return paramsObj;
        },
      },
    });
    const exportColumnsVisible = ref(false);
    const valueConfig = ref({
      dictId: '',
      dictCode: '',
      visible: false,
    });
    const editFormConfig = ref({
      visible: false,
      formData: {},
    });
    const handleValueItem = (row: IGetDictionaryListRes['list'][0]) => {
      valueConfig.value.dictId = row.dictId;
      valueConfig.value.dictCode = row.code;
      valueConfig.value.visible = true;
    };
    const handleEditItem = async (row?: IGetDictionaryListRes['list'][0]) => {
      try {
        editFormConfig.value.formData = row || {};
        editFormConfig.value.visible = true;
      } catch (error) {
        console.log('同步失败', error);
      }
    };
    const handleDelItem = async (code: string) => {
      try {
        await ElMessageBox.confirm('请确认是否删除', '提示');
        await delDictionary({
          dictCode: code,
        });
        ElMessage.success('删除成功');
        handleSearch(params.value.pageNum);
      } catch (error) {
        console.log('同步失败', error);
      }
    };
    // 更新字典状态
    const handleUpdateStatus = async (row: IGetDictionaryListRes['list'][0]) => {
      try {
        await updateDictionaryStatus({
          id: row.dictId,
          isEnable:
            row.isEnable === DICTIONARY_STATUS_ENUM.ENABLE
              ? DICTIONARY_STATUS_ENUM.DISENABLE
              : DICTIONARY_STATUS_ENUM.ENABLE,
        });
        ElMessage.success('操作成功');
        handleSearch(params.value.pageNum);
      } catch (error) {
        console.log('更新状态失败', error);
      }
    };
    const handleExportExcel = async () => {
      exportColumnsVisible.value = true;
    };
    const handleImportExcel = async (e: Event) => {
      try {
        const el: any = e.target;
        const { files }: { files: FileList; } = el;
        if (files.length === 0) return;
        const formData = new FormData();
        const file = files[0];
        if (!['xlsx', 'xls'].includes(file.name.split('.').pop()!)) {
          ElMessage.error('必须上传excel文件');
          return;
        }
        formData.append('file', file);
        el.value = null;
        await uploadDictionaryValueExcel(formData);
        ElMessage.success('导入成功');
      } catch (error) {
        console.log(error, '上传excel失败');
      }
    };
    const handleDownloadTemplate = async () => {
      try {
        const a = document.createElement('a');
        // eslint-disable-next-line max-len
        a.href = 'https://cloud-chuangxin.oss-cn-shenzhen.aliyuncs.com/dictValueInfo.xls?versionId=CAEQIxiBgMDLos7D1xciIDdlNjFiY2ExMmFhNzQ3ZmQ4ZTg5ODJiZjQ2Y2FiMTg4';
        a.download = '数据字典导入模板.xlsx';
        a.click();
      } catch (error) {
        console.log(error);
      }
    };
    const handleChangeSelectedKeys = (keys: IGetDictionaryListRes['list']) => {
      selectedKeys.value = keys.map(v => v.code);
    };
    handleSearch();

    const permissionConfig = usePermissionConfig();

    return {
      selectedKeys,
      handleChangeSelectedKeys,
      handleDownloadTemplate,
      exportColumnsVisible,
      handleImportExcel,
      handleExportExcel,
      handleUpdateStatus,
      valueConfig,
      handleValueItem,
      handleDelItem,
      editFormConfig,
      handleEditItem,
      params,
      tableTotal,
      tableData,
      tableLoading,
      handleSearch,
      handleReset,
      handleSizeChange,
      handleCurrentChange,
      GOODS_SERVICE_ERROR_LOG_STATE_LIST,
      permissionConfig,
    };
  },
  render() {
    const slots = {
      fheader: () => (
        <el-form
          size="default"
          onKeyup={withKeys(() => this.handleSearch(), ['enter'])}
          label-width="130px"
        >
          <el-form-item label="字典编号" label-width="70px">
            <el-input v-model={[this.params.code, ['trim']]} placeholder="请输入字典编号" />
          </el-form-item>
          <el-form-item label="字典名称" label-width="70px">
            <el-input v-model={[this.params.name, ['trim']]} placeholder="请输入字典名称" />
          </el-form-item>
          <el-form-item label-width="0" class="margin-left-20">
            <el-button type="primary" size="default" onClick={() => this.handleSearch()}>
              查询
            </el-button>
            <el-button size="default" onClick={() => this.handleReset()}>
              重置
            </el-button>
          </el-form-item>
        </el-form>
      ),
      header: () => (
        <el-row>
          {this.$has(this.permissionConfig.CREATE) && <el-button
            type="primary"
            size="default"
            onClick={() => this.handleEditItem()}
          >
            新增
          </el-button>}
          {this.$has(this.permissionConfig.OUTPUT) && <el-button
            disabled={this.selectedKeys.length === 0}
            type="primary"
            plain
            size="default"
            onClick={() => this.handleExportExcel()}
          >
            导出字典(已选中{this.selectedKeys.length})
          </el-button>}
          {this.$has(this.permissionConfig.IMPORT) && <el-button
            type="success"
            plain
            size="default"
            class={styles.upload_button}
          >
            导入字典
            <input type="file" class={styles.upload} onChange={this.handleImportExcel} />
          </el-button>}
          {this.$has(this.permissionConfig.DOWNLOAD) && <el-button
            size="default"
            onClick={this.handleDownloadTemplate}
          >
            下载导入模板
          </el-button>}
        </el-row>
      ),
      main: ({ height }: any) => (
        <custom-table
          size="medium"
          data={this.tableData}
          columns={renderColumns(
            this.handleEditItem,
            this.handleDelItem,
            this.handleValueItem,
            this.handleUpdateStatus,
            this.$has(this.permissionConfig.UPDATE),
            this.$has(this.permissionConfig.DELETE),
            this.$has(this.permissionConfig.VALUEMANAGE),
          )}
          height={height}
          key={height}
          v-loading={this.tableLoading}
          onSelectionChange={this.handleChangeSelectedKeys}
        />
      ),
      ffooter: () => (
        <el-row style="width: 100%;padding-top:10px" type="flex" justify="end">
          <pagination
            total={this.tableTotal}
            current-page={this.params.pageNum}
            size={this.params.pageSize}
            onCurrentChange={this.handleCurrentChange}
            onSizeChange={this.handleSizeChange}
            page-sizes={[10, 20, 30, 50, 100, 200]}
          ></pagination>
        </el-row>
      ),
    };
    return (
      <div class="error-logs">
        <app-page v-slots={slots} />
        <edit-dialog
          v-model={[this.editFormConfig.visible, 'visible']}
          formData={this.editFormConfig.formData}
          onSuccess={() => this.handleSearch(this.params.pageNum)}
        />
        <export-columns-dialog
          v-model={[this.exportColumnsVisible, 'visible']}
          dictCodes={this.selectedKeys}
        />
        <value-list-dialog
          v-model={[this.valueConfig.visible, 'visible']}
          dictId={this.valueConfig.dictId}
          dictCode={this.valueConfig.dictCode}
        />
      </div>
    );
  },
});
